import {
  getRace,
  isString,
} from './helper';

import { channelMax } from './common';

export function rgbToHsl(...rgb) {
  const [r, g, b] = rgb.map(val => val / channelMax);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;

    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    if (max === r) {
      h = ((g - b) / d) + (g < b ? 6 : 0);
    } else if (max === g) {
      h = ((b - r) / d) + 2;
    } else if (max === b) {
      h = ((r - g) / d) + 4;
    }

    h /= 6;
  }

  return [h, s, l];
}

export function hue2rgb(p1, q1, h) {
  let t = h;

  if (t < 0) {
    t += 1;
  }

  if (t > 1) {
    t -= 1;
  }

  if (t < 1 / 6) {
    return p1 + (((q1 - p1) * 6) * t);
  }

  if (t < 1 / 2) {
    return q1;
  }

  if (t < 2 / 3) {
    return p1 + ((q1 - p1) * (((2 / 3) - t) * 6));
  }

  return p1;
}

export function hslToRgb(h, s, l) {
  let r = l;
  let g = l;
  let b = l;

  if (s !== 0) {
    const q = l < 0.5 ? l * (1 + s) : l + (s - (l * s));
    const p = (2 * l) - q;

    r = hue2rgb(p, q, h + (1 / 3));
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - (1 / 3));
  }

  return [r * channelMax, g * channelMax, b * channelMax];
}

export function hexToArray(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ] : null;
}

export function arrayToHex([r, g, b]) {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function hexToNumber(hex) {
  return parseInt(hex.slice(1), 16);
}

export function mixColors(colorsMap) {
  return arrayToHex(colorsMap.reduce((resultColor, item) => {
    const color = hexToArray(item.color);

    return resultColor.map((rgbColor, rgbIndex) =>
      rgbColor + (color[rgbIndex] * item.weight));
  }, [0, 0, 0]).map(resultColor => Math.floor(resultColor)));
}

export function mixColorsByWeight(racesWeights) {
  return mixColors(racesWeights.map((weight, index) => ({
    ...getRace(index),
    weight,
  })));
}

export function safeMixColorsByWeight(source) {
  return isString(source) ? source : mixColorsByWeight(source);
}

export function getLightenColor(color, lightness) {
  const [r, g, b] = hexToArray(color);
  const [h, s] = rgbToHsl(r, g, b);

  return arrayToHex(hslToRgb(h, r === g && r === b ? s : 1, lightness)
    .map(Math.abs)).split('.')[0];
}

export function getSaturatedColor(color, saturation) {
  const [r, g, b] = hexToArray(color);
  const [h, s, l] = rgbToHsl(r, g, b);

  if (r === g || s > saturation) {
    return color;
  }

  return arrayToHex(hslToRgb(h, saturation, l).map(Math.abs)).split('.')[0];
}

export function getDragonColor(races, saturation = 0.8) {
  return getSaturatedColor(safeMixColorsByWeight(races), saturation);
}
